import { Button } from "primereact/button";
import { useLocation, useNavigate } from "react-router-dom";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { useTagList } from "../../api/queries/tag/list";
import { TagListItem, UpdateTagPayload } from "../../api/types/tag";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { formatDateTime, parseQueryString } from "../../common/utils";
import { InputText } from "primereact/inputtext";
import { updateTagRequest } from "../../api/queries/tag/update";
import { appToastContext } from "../../context/toast";
import { useMutation } from "react-query";
import { deleteTagRequest } from "../../api/queries/tag/delete";
import styles from "../Institutions/styles.module.css";
import { useDebounce } from "../../common/useDebounce";

export default function TagList() {
  const navigate = useNavigate();
  const [tags, setTags] = useState<TagListItem[]>();
  const queryParams = parseQueryString(useLocation().search);
  const [search, setSearch] = useState<string>(queryParams.search || "");
  const searchDebounced = useDebounce(search, 500);
  const { ref: appToastRef } = useContext(appToastContext);

  const updateTagMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTagPayload }) =>
      updateTagRequest(id, payload),
    onSuccess: () => {
      if (appToastRef?.current) {
        appToastRef.current.show({
          severity: "success",
          summary: "Tag updated successfully.",
        });
      }
    },
    onError: (error: any) => {
      if (appToastRef?.current) {
        appToastRef.current.show({
          severity: "success",
          summary: "Update failed: " + error.message,
        });
      }
    },
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const { data, isLoading } = useTagList(
    searchDebounced ? { search: searchDebounced } : undefined,
  );
  useEffect(() => {
    setTags(data?.data.results);
  }, [data]);

  const tCreatedAt = (row: TagListItem) => formatDateTime(row.created_at);

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback!(e.target.value)
        }
      />
    );
  };

  const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
    let { newData, index } = e;

    setTags((prevTags = []) => {
      const updatedTags = [...(prevTags ?? [])];
      updatedTags[index] = newData as TagListItem;
      return updatedTags;
    });

    await updateTagMutation.mutateAsync({
      id: newData.id,
      payload: { name: newData.name },
    });
  };

  const deleteTagMutation = useMutation({
    mutationFn: (id: string) => deleteTagRequest(id),
    onSuccess: () => {
      if (appToastRef?.current) {
        appToastRef.current.show({
          severity: "success",
          summary: "Tag deleted successfully.",
        });
      }
    },
    onError: (error: any) => {
      if (appToastRef?.current) {
        appToastRef.current.show({
          severity: "success",
          summary: "Delete failed: " + error.message,
        });
      }
    },
  });

  const handleDelete = async (rowData: TagListItem) => {
    await deleteTagMutation.mutateAsync(rowData.id);
    setTags((prevTags) =>
      (prevTags ?? []).filter((tag) => tag.id !== rowData.id),
    );
  };

  return (
    <div className={"col-12"}>
      <div className="flex py-3">
        <h1 className="m-0">Tags</h1>

        <div className="flex gap-1 ml-auto">
          <Button
            onClick={() => navigate("/admin/tags/new")}
            className="p-button p-button-main p-button-sm"
            label="Add new"
          />
        </div>
      </div>

      <div className="mb-3">
        <InputText
          value={search}
          onChange={onSearchChange}
          placeholder="Search"
          className={styles.searchInput}
        />
      </div>
      <DataTable
        value={tags}
        loading={isLoading}
        emptyMessage={"No records"}
        stripedRows={true}
        editMode="row"
        dataKey="id"
        onRowEditComplete={onRowEditComplete}
      >
        <Column
          field="name"
          header="Name"
          editor={(options) => textEditor(options)}
        />
        <Column field="created_at" header="Created at" body={tCreatedAt} />
        <Column
          rowEditor
          header="edit"
          headerStyle={{ width: "10%", minWidth: "8rem" }}
          bodyStyle={{ textAlign: "center" }}
        ></Column>
        <Column
          header="Remove"
          body={(rowData) => (
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              onClick={() => handleDelete(rowData)}
            />
          )}
          headerStyle={{ width: "8%", minWidth: "6rem" }}
          bodyStyle={{ textAlign: "center" }}
        />
      </DataTable>
    </div>
  );
}
